import { useState, useEffect, useRef } from "react";
import { Search, Plus, User, Check, X, AlertCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientSelector({ 
  value, 
  onChange, 
  onClientSelect,
  recipient,
  onRecipientChange 
}) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [duplicateReason, setDuplicateReason] = useState("");
  const [pendingClient, setPendingClient] = useState(null);
  const wrapperRef = useRef(null);
  const searchTimeout = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load selected client if value changes
  useEffect(() => {
    if (value && !selectedClient) {
      loadClient(value);
    }
  }, [value]);

  const loadClient = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}`);
      setSelectedClient(response.data);
    } catch (error) {
      console.error("Failed to load client:", error);
    }
  };

  const searchClients = async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/search?q=${encodeURIComponent(query)}`);
      setResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setShowDropdown(true);
    
    // Debounce search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      searchClients(query);
    }, 300);
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearch("");
    setShowDropdown(false);
    onChange(client.id);
    
    // Auto-fill recipient fields
    if (onRecipientChange) {
      onRecipientChange({
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        address: client.address || "",
        company: client.company || ""
      });
    }
    
    if (onClientSelect) {
      onClientSelect(client);
    }
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setSearch("");
    onChange("");
  };

  const handleCreateNewClient = () => {
    setShowNewClientDialog(true);
    setShowDropdown(false);
  };

  const checkAndCreateClient = async (clientData) => {
    try {
      // Check for duplicates
      const checkResponse = await axios.post(`${API}/clients/check-duplicate`, clientData);
      
      if (checkResponse.data.has_duplicate) {
        setDuplicateMatches(checkResponse.data.potential_matches);
        setDuplicateReason(checkResponse.data.match_reason);
        setPendingClient(clientData);
        setShowDuplicateDialog(true);
        return;
      }
      
      // No duplicate, create client
      await createClientDirectly(clientData);
    } catch (error) {
      console.error("Failed to check/create client:", error);
      toast.error("Failed to create client");
    }
  };

  const createClientDirectly = async (clientData) => {
    try {
      const response = await axios.post(`${API}/clients`, clientData);
      const newClient = response.data;
      
      handleSelectClient(newClient);
      setShowNewClientDialog(false);
      setShowDuplicateDialog(false);
      toast.success("Client created");
    } catch (error) {
      console.error("Failed to create client:", error);
      toast.error("Failed to create client");
    }
  };

  const handleUseDuplicate = (client) => {
    handleSelectClient(client);
    setShowDuplicateDialog(false);
    setPendingClient(null);
  };

  const handleCreateAnyway = async () => {
    if (pendingClient) {
      await createClientDirectly(pendingClient);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Label className="text-xs text-muted-foreground mb-1 block">Client</Label>
      
      {selectedClient ? (
        <div className="flex items-center justify-between p-2 bg-muted/30 rounded-md border border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">{selectedClient.name}</p>
              <p className="text-xs text-muted-foreground">
                {selectedClient.email || selectedClient.phone || selectedClient.company || "No contact info"}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0"
            onClick={handleClearClient}
            data-testid="clear-client"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={handleSearchChange}
            onFocus={() => setShowDropdown(true)}
            placeholder="Search clients by name, email, phone..."
            className="pl-9"
            data-testid="client-search"
          />
          
          {/* Dropdown */}
          {showDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-64 overflow-auto">
              {loading ? (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              ) : results.length > 0 ? (
                <>
                  {results.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className="w-full flex items-center gap-3 p-2 hover:bg-muted/50 transition-colors text-left"
                      data-testid={`client-option-${client.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{client.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {[client.email, client.phone, client.company].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                    </button>
                  ))}
                  <div className="border-t border-border">
                    <button
                      onClick={handleCreateNewClient}
                      className="w-full flex items-center gap-2 p-2 text-sm text-primary hover:bg-muted/50 transition-colors"
                      data-testid="create-new-client-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create new client
                    </button>
                  </div>
                </>
              ) : search.length >= 2 ? (
                <div className="p-3">
                  <p className="text-sm text-muted-foreground mb-2">No clients found</p>
                  <button
                    onClick={handleCreateNewClient}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                    data-testid="create-new-client-empty"
                  >
                    <Plus className="w-4 h-4" />
                    Create "{search}" as new client
                  </button>
                </div>
              ) : (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  Type to search clients
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Client Dialog */}
      <NewClientDialog
        open={showNewClientDialog}
        onClose={() => setShowNewClientDialog(false)}
        initialName={search}
        onSubmit={checkAndCreateClient}
      />

      {/* Duplicate Warning Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Possible duplicate
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-muted-foreground">
            {duplicateReason}. Would you like to use the existing client?
          </p>
          
          <div className="space-y-2 my-4">
            {duplicateMatches.map((client) => (
              <button
                key={client.id}
                onClick={() => handleUseDuplicate(client)}
                className="w-full flex items-center justify-between p-3 bg-muted/30 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium">{client.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[client.email, client.phone].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <span className="text-xs text-primary">Use this</span>
              </button>
            ))}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleCreateAnyway}>
              Create new anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewClientDialog({ open, onClose, initialName, onSubmit }) {
  const [form, setForm] = useState({
    name: initialName || "",
    email: "",
    phone: "",
    address: "",
    company: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initialName) {
      setForm(prev => ({ ...prev, name: initialName }));
    }
  }, [open, initialName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle>Create client</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Client name"
              className="mt-1"
              data-testid="new-client-name"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                className="mt-1"
                data-testid="new-client-email"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone number"
                className="mt-1"
                data-testid="new-client-phone"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-xs text-muted-foreground">Company</Label>
            <Input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="Company name"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label className="text-xs text-muted-foreground">Address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Address"
              className="mt-1"
            />
          </div>
          
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-new-client">
              {saving ? "Creating..." : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
